import { useCallback, useEffect, useState } from 'react';
import { hojeBrasilia, lerPrecos } from '../../central';
import type { OrdemDosPrecos, Precos, SituacaoDoPreco } from '../../central';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import {
  MODELOS_PARTES,
  MODELOS_TEMPO,
  fmt,
  partes,
  serieNoTempo,
} from '../../design/graficos/modelos';
import { reais } from '../../produtos';
import { BarraDeFiltros, Kpi, irPara, lerFiltros, textoDoPeriodo } from '../paineis/pecasDaVisao';
import type { Sessao } from '../../auth';
import { AlertasDeMargem } from './AlertasDeMargem';
import { CentralDeDescontosTela } from './Descontos';
import type { VisaoDaCentral } from './Descontos';
import { SubnavDeVendas } from './SubnavDeVendas';
import './central.css';

/**
 * TELA 14C — PREÇOS & MARGEM (área restrita). Por produto, no período: preço médio, menor e último
 * praticados, PMC, custo do DIA DA VENDA (API do HOS), margem, lucro bruto, desconto médio e quantas
 * vezes saiu abaixo do custo. Custo e margem só para quem configura produto; para os outros, só os
 * preços. Filtros no endereço: período, situação, ordem e busca.
 */

const SITUACOES: readonly [SituacaoDoPreco, string][] = [
  ['todos', 'Todos'],
  ['negativa', 'Margem negativa'],
  ['baixa', 'Margem abaixo de 10%'],
  ['abaixo_do_custo', 'Vendido abaixo do custo'],
  ['sem_custo', 'Sem custo'],
];
const ORDENS: readonly [OrdemDosPrecos, string][] = [
  ['faturamento', 'Maior faturamento'],
  ['lucro', 'Maior lucro'],
  ['margem', 'Menor margem'],
  ['quantidade', 'Mais vendidos'],
  ['desconto', 'Maior desconto'],
];

const margemTexto = (m: number | null) =>
  m === null ? '—' : Math.abs(m) > 999 ? (m < 0 ? '< −999%' : '> 999%') : fmt(m, 'pct');

const VISOES: readonly [VisaoDaCentral | 'precos' | 'alertas', string][] = [
  ['precos', 'Preços & margem'],
  ['descontos', 'Descontos'],
  ['balconistas', 'Balconistas'],
  ['alertas', 'Alertas'],
];

/** Preços & margem (dono, 28/09): a visão de sempre + a central de descontos e a de balconistas. */
function SeletorDeVisao({ atual, parametros }: { atual: string; parametros: URLSearchParams }) {
  return (
    <div className="cv-segmento dc-visoes" role="tablist" aria-label="Visão">
      {VISOES.map(([v, r]) => {
        // troca de visão mantém só o período
        const q = new URLSearchParams();
        for (const k of ['p', 'de', 'ate']) {
          const x = parametros.get(k);
          if (x) q.set(k, x);
        }
        if (v !== 'precos') q.set('visao', v);
        const s = q.toString();
        return (
          <a
            key={v}
            role="tab"
            aria-selected={atual === v}
            className={atual === v ? 'cv-sel' : ''}
            href={`#/precos${s ? `?${s}` : ''}`}
          >
            {r}
          </a>
        );
      })}
    </div>
  );
}

export function PrecosEMargem({
  veCusto,
  parametros,
  sessao,
}: {
  veCusto: boolean;
  parametros: URLSearchParams;
  sessao: Sessao;
}) {
  const visao = parametros.get('visao');
  // Alerta de margem e desconto por balconista (Carlos, 28/09)
  if (visao === 'alertas')
    return (
      <section className="cv">
        <SubnavDeVendas atual="precos" direita="margem e desconto por balconista" />
        <div className="cv-corpo vg dc-cabeca">
          <header className="vg-topo">
            <div className="vg-titulo">
              <h1>Alerta de margem e desconto</h1>
              <div className="vg-contexto">quem está no vermelho em margem ou desconto</div>
            </div>
            <SeletorDeVisao atual="alertas" parametros={parametros} />
          </header>
        </div>
        <AlertasDeMargem sessao={sessao} />
      </section>
    );
  if (visao === 'descontos' || visao === 'balconistas')
    return (
      <section className="cv">
        <SubnavDeVendas
          atual="precos"
          direita={veCusto ? 'custo do dia da venda' : 'custo restrito'}
        />
        <div className="cv-corpo vg dc-cabeca">
          <header className="vg-topo">
            <div className="vg-titulo">
              <h1>{visao === 'descontos' ? 'Descontos' : 'Performance por balconista'}</h1>
              <div className="vg-contexto">
                {visao === 'descontos'
                  ? 'quanto, onde e quem deu desconto — até o cupom'
                  : 'quem vende, quem dá desconto e quem gera resultado'}
              </div>
            </div>
            <SeletorDeVisao atual={visao} parametros={parametros} />
          </header>
        </div>
        <CentralDeDescontosTela visao={visao} parametros={parametros} veCusto={veCusto} />
      </section>
    );
  return <PrecosDeSempre veCusto={veCusto} parametros={parametros} />;
}

function PrecosDeSempre({
  veCusto,
  parametros,
}: {
  veCusto: boolean;
  parametros: URLSearchParams;
}) {
  const hoje = hojeBrasilia();
  const q = new URLSearchParams(parametros);
  if (!q.get('p')) q.set('p', 'mes');
  const f = lerFiltros(q, hoje);
  const { de, ate } = f.periodo;
  const situacao = (
    SITUACOES.some(([s]) => s === parametros.get('situacao')) ? parametros.get('situacao') : 'todos'
  ) as SituacaoDoPreco;
  const ordem = (
    ORDENS.some(([o]) => o === parametros.get('ordem')) ? parametros.get('ordem') : 'faturamento'
  ) as OrdemDosPrecos;
  const busca = (parametros.get('busca') ?? '').slice(0, 80);
  const extras: {
    situacao: SituacaoDoPreco | null;
    ordem: OrdemDosPrecos | null;
    busca: string | null;
  } = {
    situacao: situacao === 'todos' ? null : situacao,
    ordem: ordem === 'faturamento' ? null : ordem,
    busca: busca || null,
  };
  const ir = (mudar: Partial<typeof extras>) =>
    irPara({ filtro: f.filtro, periodo: f.periodo, pessoa: null }, 'precos', {
      ...extras,
      ...mudar,
    });
  const [texto, setTexto] = useState(busca);
  useEffect(() => setTexto(busca), [busca]);
  const [d, setD] = useState<Precos | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerPrecos({ de, ate, busca: busca || null, situacao, ordem }).then((r) =>
      r.ok ? setD(r.dados) : setErro(true),
    );
  }, [de, ate, busca, situacao, ordem]);
  useEffect(carregar, [carregar]);

  const quando = textoDoPeriodo(f.filtro, f.periodo, hoje);
  const r = d?.resumo;
  const comMargem = d ? d.porGrupo.filter((g) => g.margem !== null) : [];
  const piorGrupo = [...comMargem].sort((a, b) => (a.margem ?? 0) - (b.margem ?? 0))[0];
  const melhorGrupo = [...comMargem].sort((a, b) => (b.margem ?? 0) - (a.margem ?? 0))[0];
  const neg = d?.distribuicao[0];
  const baixa = d?.distribuicao[1];

  return (
    <section className="cv">
      <SubnavDeVendas
        atual="precos"
        direita={veCusto ? 'custo do dia da venda' : 'custo restrito'}
      />
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <h1>Preços &amp; margem</h1>
            <div className="vg-contexto">
              {r ? `${fmt(r.produtos, 'inteiro')} produtos vendidos · ${quando}` : quando}
            </div>
          </div>
          <SeletorDeVisao atual="precos" parametros={parametros} />
        </header>

        <BarraDeFiltros f={f} hoje={hoje} rota="precos" extras={extras} />

        {veCusto ? (
          <div className="vg-kpis cv-kpis-5">
            <Kpi
              rotulo={`Margem · ${quando}`}
              icone="gauge"
              valor={d ? (d.margem.disponivel ? fmt(Number(d.margem.valor), 'pct') : '—') : null}
              variacao={null}
              sub={d ? (d.margem.disponivel ? d.margem.fonte : d.margem.porque) : null}
              link="#/precos"
              destaque
            />
            <Kpi
              rotulo="Lucro bruto"
              icone="money"
              valor={r ? (r.lucro ? fmt(Number(r.lucro), 'reais') : '—') : null}
              variacao={null}
              sub={r ? `sobre ${fmt(Number(r.faturamentoComCusto), 'reais')} com custo` : null}
              link="#/precos"
            />
            <Kpi
              rotulo="Margem negativa"
              icone="warn"
              valor={r ? `${fmt(r.negativas, 'inteiro')} produtos` : null}
              variacao={null}
              sub={r ? `${fmt(r.baixas, 'inteiro')} abaixo de 10%` : null}
              link="#/precos?situacao=negativa&ordem=margem"
            />
            <Kpi
              rotulo="Vendas abaixo do custo"
              icone="arrow"
              valor={r ? fmt(r.vendasAbaixoDoCusto, 'inteiro') : null}
              variacao={null}
              sub={r ? 'itens vendidos por menos que o custo do dia' : null}
              link="#/precos?situacao=abaixo_do_custo"
            />
            <Kpi
              rotulo="Sem custo"
              icone="lock"
              valor={r ? `${fmt(r.semCusto, 'inteiro')} produtos` : null}
              variacao={null}
              sub={
                r ? 'venda sem custo: antes de 12/09 a fonte não trazia, ou custo de R$ 0,01' : null
              }
              link="#/precos?situacao=sem_custo"
            />
          </div>
        ) : (
          <div className="cv-aviso">
            <Icone nome="lock" tamanho={14} traco={1.7} />
            Custo, margem e lucro são visíveis só para quem configura produtos. Aqui ficam os preços
            praticados.
          </div>
        )}

        {veCusto && d && d.margem.disponivel && (
          <div className="vg-linha cv-linha-2">
            <CartaoDeGrafico
              id="cv-margem-grupo"
              titulo="Margem por grupo"
              numero={
                melhorGrupo ? `${melhorGrupo.grupo} · ${fmt(melhorGrupo.margem, 'pct')}` : '—'
              }
              conclusao={
                piorGrupo && melhorGrupo ? (
                  <>
                    Menor margem: <strong>{piorGrupo.grupo}</strong> ({fmt(piorGrupo.margem, 'pct')}
                    ); maior: <strong>{melhorGrupo.grupo}</strong> ({fmt(melhorGrupo.margem, 'pct')}
                    ).
                  </>
                ) : (
                  'Sem grupo com custo no período.'
                )
              }
              modelos={MODELOS_PARTES.filter(
                ([m]) => m === 'barras' || m === 'colunas' || m === 'radial',
              )}
              padrao="barras"
              desenhar={(m) =>
                partes(m, {
                  itens: comMargem.map((g) => ({ nome: g.grupo, valor: g.margem ?? 0 })),
                  formato: 'pct',
                })
              }
              fonte="Fonte: custo do dia da venda (API do HOS)"
              altura={260}
            />
            <CartaoDeGrafico
              id="cv-margem-faixas"
              titulo="Onde está o faturamento, por margem"
              numero={fmt(Number(r?.faturamentoComCusto ?? 0), 'reais')}
              conclusao={
                neg ? (
                  <>
                    <strong>{fmt(neg.faturamento + (baixa?.faturamento ?? 0), 'reais')}</strong>{' '}
                    foram vendidos com margem abaixo de 10% (
                    {fmt(neg.produtos + (baixa?.produtos ?? 0), 'inteiro')} produtos). É por aí que
                    a margem escapa.
                  </>
                ) : (
                  'Sem custo no período.'
                )
              }
              modelos={MODELOS_TEMPO.filter(
                ([m]) => m === 'colunas' || m === 'linha' || m === 'area',
              )}
              padrao="colunas"
              desenhar={(m) =>
                serieNoTempo(m, {
                  cats: d.distribuicao.map((x) => x.faixa),
                  atual: d.distribuicao.map((x) => x.faturamento),
                  anterior: d.distribuicao.map(() => null),
                  nome: 'Faturamento',
                  formato: 'reais',
                })
              }
              fonte="Fonte: custo do dia da venda (API do HOS)"
              altura={260}
            />
          </div>
        )}

        <div className="vg-filtros cv-filtros-lista" role="toolbar" aria-label="Filtros da lista">
          {veCusto && (
            <label className="vg-pessoa">
              <Icone nome="filter" tamanho={14} traco={1.6} />
              <select
                value={situacao}
                aria-label="Situação"
                onChange={(e) =>
                  ir({
                    situacao:
                      e.target.value === 'todos' ? null : (e.target.value as SituacaoDoPreco),
                  })
                }
              >
                {SITUACOES.map(([s, rot]) => (
                  <option key={s} value={s}>
                    {rot}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="vg-pessoa">
            <Icone nome="arrow" tamanho={14} traco={1.6} />
            <select
              value={ordem}
              aria-label="Ordenar"
              onChange={(e) =>
                ir({
                  ordem:
                    e.target.value === 'faturamento' ? null : (e.target.value as OrdemDosPrecos),
                })
              }
            >
              {ORDENS.filter(([o]) => veCusto || (o !== 'margem' && o !== 'lucro')).map(
                ([o, rot]) => (
                  <option key={o} value={o}>
                    {rot}
                  </option>
                ),
              )}
            </select>
          </label>
          <form
            className="cv-busca-nova"
            onSubmit={(e) => {
              e.preventDefault();
              ir({ busca: texto.trim() || null });
            }}
          >
            <Icone nome="search" tamanho={15} traco={1.7} />
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Produto, código ou EAN"
              aria-label="Buscar produto, código ou EAN"
            />
            {busca && (
              <button type="button" aria-label="Limpar busca" onClick={() => ir({ busca: null })}>
                <Icone nome="x" tamanho={13} traco={1.8} />
              </button>
            )}
          </form>
        </div>

        <div className="vg-bloco cv-lista">
          {erro ? (
            <ErroDeArea oQue="os preços" aoTentar={carregar} />
          ) : !d ? (
            <div className="vg-lista-osso" aria-busy="true">
              {[0, 1, 2, 3, 4, 5].map((k) => (
                <i key={k} />
              ))}
            </div>
          ) : d.produtos.length === 0 ? (
            <p className="cv-vazio">
              {d.fonteDisponivel
                ? 'Nenhum produto com estes filtros.'
                : 'Nenhuma venda no período.'}
            </p>
          ) : (
            <div className="cv-rolagem">
              <table className="cv-tabela">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th className="cv-dir">Qtd</th>
                    <th className="cv-dir">Faturamento</th>
                    <th className="cv-dir">Preço médio</th>
                    <th className="cv-dir">Menor</th>
                    <th className="cv-dir">PMC</th>
                    {veCusto && (
                      <>
                        <th className="cv-dir">Custo</th>
                        <th className="cv-dir">Margem</th>
                        <th className="cv-dir">Lucro</th>
                      </>
                    )}
                    <th className="cv-dir">Desconto</th>
                    {veCusto && <th className="cv-dir">Abaixo do custo</th>}
                  </tr>
                </thead>
                <tbody>
                  {d.produtos.map((p) => (
                    <tr key={p.productId}>
                      <td>
                        <div className="cv-forte">{p.produto}</div>
                        <div className="cv-mono cv-cinza cv-peq">
                          {[p.codigo, p.ean, p.grupo].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </td>
                      <td className="cv-dir">{fmt(p.quantidade, 'inteiro')}</td>
                      <td className="cv-dir cv-forte">{fmt(Number(p.faturamento), 'reais')}</td>
                      <td className="cv-dir">{reais(p.medio30)}</td>
                      <td className="cv-dir cv-cinza">{reais(p.menor)}</td>
                      <td className="cv-dir cv-cinza">{reais(p.pmc)}</td>
                      {veCusto && (
                        <>
                          <td
                            className="cv-dir cv-cinza"
                            title={
                              p.fonteDoCusto === 'venda'
                                ? 'custo médio do dia da venda (API do HOS)'
                                : p.fonteDoCusto === 'cadastro'
                                  ? 'custo do cadastro atual (as vendas não trouxeram custo)'
                                  : undefined
                            }
                          >
                            {reais(p.custo)}
                            {p.fonteDoCusto === 'cadastro' && <sup> *</sup>}
                          </td>
                          <td
                            className={`cv-dir cv-forte ${p.margem !== null && p.margem < 0 ? 'cv-neg' : p.margem !== null && p.margem < 10 ? 'cv-alerta-txt' : ''}`}
                          >
                            {margemTexto(p.margem)}
                          </td>
                          <td
                            className={`cv-dir ${p.lucro && Number(p.lucro) < 0 ? 'cv-neg' : ''}`}
                          >
                            {p.lucro === null ? '—' : fmt(Number(p.lucro), 'reais')}
                          </td>
                        </>
                      )}
                      <td className="cv-dir">
                        {p.descontoMedio === null ? '—' : fmt(p.descontoMedio, 'pct')}
                      </td>
                      {veCusto && (
                        <td className="cv-dir">
                          {p.abaixoDoCusto ? (
                            <span className="cv-contador">{p.abaixoDoCusto}×</span>
                          ) : (
                            <span className="cv-cinza">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="cv-paginacao">
            <span>
              {d
                ? d.total > d.produtos.length
                  ? `os ${fmt(d.produtos.length, 'inteiro')} primeiros de ${fmt(d.total, 'inteiro')} — use a busca ou os filtros`
                  : `${fmt(d.total, 'inteiro')} produtos`
                : ''}
            </span>
            <span>
              {veCusto
                ? 'margem = (preço − custo do dia) ÷ preço · * custo do cadastro'
                : 'preços praticados nas vendas do HOS'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
